<template>
  <!--------------------------------------
    Individual data points in the <ProfileDataElement> grid elements.
  ---------------------------------------->
  <div class="profile-data-point">
    <div class="inline-data-point-div" v-if="inlinePoint === true">
      <v-icon v-if="icon" left :color="icon === 'circle' ? '#888' : iconColor" class="data-point-icon"  :size="icon === 'circle' ? 6 : 25">
          {{ icons[icon] }}
      </v-icon>
      <span class="text" v-if="introText">{{ introText }}</span><span class="inline-data-point" v-if="dataPoint">{{ dataPoint }}</span><span v-if="text">{{ text }}</span><span class="inline-data-point" v-if="comparisonDataPoint">{{ comparisonDataPoint }}</span><span v-if="afterText">{{ afterText }}</span>
    </div>
    <div v-else>
      <div class="intro" v-if="introText">{{ introText }}</div>
      <div class="point-and-text" v-if="pointOnRight !== true" :class="sideBySide === true ? 'side-by-side' : ''">
        <div class="data-point" :class="italicDataPoint === true ? 'italic' : ''">
          <div v-if="icon"><v-icon left :color="icon === 'circle' ? '#888' : iconColor" class="data-point-icon"  :size="icon === 'circle' ? 6 : 25">
            {{ icons[icon] }}
          </v-icon></div>
          <div>{{ dataPoint }}</div>
        </div>
        <div class="text" :class="{ italic: italicText === true, indent: indentText === true}">{{ text }}</div>
      </div>
      <div class="point-and-text side-by-side point-on-right" v-if="pointOnRight === true">
        <div class="text" :class="{ italic: italicText === true, indent: indentText === true}">{{ text }}</div>
        <div class="data-point-right" :class="italicDataPoint === true ? 'italic' : ''">{{ dataPoint }}
        </div>
      </div>
      <div v-if="comparisonDataPoint && comparisonText" class="point-and-text" :class="sideBySide === true? 'side-by-side' : ''">
        <div class="data-point" :class="italicDataPoint === true ? 'italic' : ''">
          <div>{{ comparisonDataPoint }}</div>
        </div>
        <div class="text" :class="italicText === true ? 'italic' : ''">{{ comparisonText }}</div>
      </div>
      <div class="after" v-if="afterText">{{ afterText }}</div>
    </div>
  </div>
</template>

<script>
import { mdiHumanMaleFemale, mdiImageBrokenVariant, mdiHumanCane, mdiStar, mdiTractorVariant, mdiBabyCarriage, mdiCircle } from '@mdi/js';

export default {
  name: 'ProfileDataPoint',

  props: {
    dataPoint: { type: String, required: true },
    icon: { type: String, required: false },
    introText: { type: String, required: false }, // If provided, goes above the data point and text
    text: { type: String, required: false },
    sideBySide: { type: Boolean, required: false, default: false }, // For flex-row (data point on left, text on right); otherwise defaults to flex-column
    afterText: { type: String, required: false }, // Another row of text. The primary use case is if you want the data point and some text side by side and then continue the text below that.
    comparisonDataPoint: { type: String, required: false }, // Provide along with comparisonText to show 2 data points, generally both using side-by-side layout. Use case example is showing in two rows something like "6% of tenants are represented" "66% of landlords are represented".
    comparisonText: { type: String, required: false },
    pointOnRight: { type: Boolean, required: false, default: false }, // Text followed by data point, such as a row that says "Income Maintenance  9%". Set to side-by-side class automatically.
    italicText: { type: Boolean, required: false, default: false },
    indentText: { type: Boolean, required: false, default: false },
    italicDataPoint: { type: Boolean, required: false, default: false },
    inlinePoint: { type: Boolean, required: false, default: false },
    iconColor: { type: String, required: false, default: '#001f60' }
  },

  data () {
    return {
      icons: {
        'people': mdiHumanMaleFemale,
        'star': mdiStar,
        'seniors': mdiHumanCane,
        'dv': mdiImageBrokenVariant,
        'rural': mdiTractorVariant,
        'child': mdiBabyCarriage,
        'circle': mdiCircle
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/scss/vars.scss';

.profile-data-point {
  font-size: 1.125rem;

  .text, .after {
    padding-left: 0.5rem;
  }
  .point-and-text {
    display: flex;
    flex-direction: column;
    &.side-by-side {
      flex-direction: row;
      align-items: baseline;
      
      .data-point {
        padding-right: 0.625rem;
      }
      .text {
        padding-left: 0;
      }
    }
    &.point-on-right {
      justify-content: space-between;
    }
  }
  .inline-data-point-div {
    font-weight: normal;
  }
  .intro {
    padding-bottom: 0.125rem;
  }
  .data-point-icon {
    padding-right: 0;
    margin-top: -0.125rem;
  }
  .data-point, .inline-data-point {
    color: $header-red;
    font-size: 1.25rem;
    font-weight: 400;
  }
  .data-point {
    display: flex;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }
  .inline-data-point-div .text {
    padding: 0;
  }
  .inline-data-point {
    padding: 0 0.4rem;

    &:first-child {
      padding-left: 0;
    }
  }
  .text, .intro, .after, .data-point-right {
    color: #3a3a3a;
    font-weight: normal;
  }
  .italic {
    font-style: italic;
  }
  .indent {
    margin-left: 0.4rem;
  }

  // PRINT-VERSION-SPECIFIC
  &.print-v {
    font-family: 'Work Sans', sans-serif;

    .inline-data-point-div {
      font-size: 0.75rem;
    }
    .data-point, .inline-data-point {
      font-size: 1.1rem;
    }
    .text, .intro, .after {
      font-size: 0.75rem;
      font-family: 'Roboto', sans-serif;
      color: #3a3a3a;
    }
    .point-on-right .text, .point-on-right {
      font-size: 0.85rem;
      &.indent {
        font-size: 0.75rem;
      }
    }
    .inline-data-point-div {
      font-family: 'Roboto', sans-serif;
    }
  }
  &.print-v-medium {
    font-family: 'Work Sans', sans-serif;

    .inline-data-point-div {
      font-size: 0.85rem;
    }
    .data-point, .inline-data-point {
      font-size: 1.1rem;
    }
    .text, .intro, .after {
      font-size: 0.85rem;
      font-family: 'Roboto', sans-serif;
      color: #3a3a3a;
    }
    .point-on-right .text, .point-on-right {
      font-size: 0.95rem;
      &.indent {
        font-size: 0.85rem;
      }
    }
    .inline-data-point-div {
      font-family: 'Roboto', sans-serif;
    }
  }
}
</style>